import { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Package, FileText, Settings, LogOut, ListChecks, ClipboardList,
  KanbanSquare, RefreshCw, BarChart3, ChevronDown, ChevronRight,
} from "lucide-react";

const PRIMARY = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, cap: "view_dashboard" },
  { to: "/engager/my-tasks", label: "My Tasks", icon: ClipboardList, cap: null },
  { to: "/proposals", label: "Proposals", icon: FileText, cap: "view_proposals", exactSearch: "" },
  { to: "/proposals?type=renewal", label: "Renewals", icon: RefreshCw, cap: "view_proposals", exactSearch: "type=renewal" },
  { to: "/pipeline", label: "Sales Pipeline", icon: KanbanSquare, cap: "pipeline" },
  { to: "/clients", label: "Clients", icon: Users, cap: "view_clients" },
  { to: "/engager/board", label: "Engager Board", icon: ListChecks, cap: "view_engager" },
  { to: "/onboarding-tracker", label: "Onboarding", icon: ClipboardList, cap: "view_clients" },
  { to: "/reports", label: "Reports", icon: BarChart3, cap: "view_dashboard" },
];
const SETTINGS_CHILDREN = [
  { to: "/admin", label: "Admin", icon: Settings, cap: "view_admin", end: true },
  { to: "/services", label: "Service Catalogue", icon: Package, cap: "view_catalogue" },
  { to: "/engager", label: "Engager Setup", icon: ListChecks, cap: "view_engager", end: true },
];
const SETTINGS_PATHS = ["/admin", "/services", "/engager"];

const itemCls = (active, sub) =>
  `flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-200 ${sub ? "pl-10 pr-3 py-2 text-[13px]" : "px-3 py-2.5"} ${
    active ? "bg-white/10 text-white" : "text-slate-300/90 hover:bg-white/5 hover:text-white"
  }`;

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const onSettings = loc.pathname === "/admin" || loc.pathname === "/services" || loc.pathname === "/engager";
  const [settingsOpen, setSettingsOpen] = useState(onSettings);

  const isEngager = user?.role === "engager";
  if (isEngager && !loc.pathname.startsWith("/engager")) return <Navigate to="/engager/board" replace />;

  const has = (cap) => !cap || (user?.capabilities || []).includes(cap);

  const renderItem = (n, sub) => {
    if (n.exactSearch !== undefined) {
      const onProposals = loc.pathname === "/proposals";
      const isRen = loc.search.includes("type=renewal");
      const active = onProposals && (n.exactSearch === "type=renewal" ? isRen : !isRen);
      return (
        <button key={n.label} data-testid={`nav-${n.label.split(" ")[0].toLowerCase()}`} onClick={() => navigate(n.to)} className={`w-full text-left ${itemCls(active)}`}>
          <n.icon className="w-[18px] h-[18px]" />{n.label}
        </button>
      );
    }
    return (
      <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.split(" ")[0].toLowerCase()}`} className={({ isActive }) => itemCls(isActive, sub)}>
        <n.icon className={sub ? "w-4 h-4 opacity-70" : "w-[18px] h-[18px]"} />{n.label}
      </NavLink>
    );
  };

  const settingsChildren = SETTINGS_CHILDREN.filter((c) => has(c.cap));

  return (
    <div className="min-h-screen flex bg-[#F5F7FB]">
      <aside className="w-64 flex flex-col fixed h-screen h-dvh overflow-y-auto text-slate-100" style={{ background: "linear-gradient(180deg,#0B2440 0%,#0E2C4E 55%,#0B2440 100%)" }}>
        <Link to="/dashboard" className="px-6 pt-7 pb-5 block">
          <div className="font-alt text-3xl font-extrabold tracking-tight text-white leading-none">CASS</div>
          <div className="text-[11px] text-slate-400 mt-1 italic">Giving You More Than Numbers</div>
        </Link>
        <nav className="flex-1 px-3 py-2 space-y-1">
          {PRIMARY.filter((n) => has(n.cap)).map((n) => renderItem(n))}
          {settingsChildren.length > 0 && (
            <>
              <div className="my-3 mx-3 border-t border-white/10" />
              <button data-testid="nav-settings" onClick={() => setSettingsOpen((o) => !o)} className={`w-full text-left ${itemCls(onSettings && !settingsOpen)}`}>
                <Settings className="w-[18px] h-[18px]" />Settings
                {settingsOpen ? <ChevronDown className="w-4 h-4 ml-auto" /> : <ChevronRight className="w-4 h-4 ml-auto" />}
              </button>
              {settingsOpen && settingsChildren.map((c) => renderItem(c, true))}
            </>
          )}
        </nav>
        <div className="mt-auto p-3 border-t border-white/10">
          <div className="flex items-center gap-2" data-testid="sidebar-user-tile">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate" data-testid="sidebar-user-name">{user?.name || "User"}</div>
              <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={logout} className="mt-3 flex items-center gap-2 text-xs text-slate-400 hover:text-white px-1 transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-64 min-h-screen min-w-0"><Outlet /></main>
    </div>
  );
}
